import React, { useState, useEffect, useCallback } from 'react';
import QRCode from 'qrcode';
import { AlertCircle } from 'lucide-react';
import apiClient, { API_ENDPOINTS } from '../../services/api';

interface LivenessQRCodeProps {
  sessionId: string;
  livenessSessionToken?: string;
  onComplete?: () => void;
  className?: string;
}

interface SessionStatus {
  sessionId: string;
  isActive: boolean;
}

/**
 * A component to display QR code for liveness check that can be embedded in other applications
 */
const LivenessQRCode: React.FC<LivenessQRCodeProps> = ({
  sessionId,
  livenessSessionToken,
  onComplete,
  className = ''
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [generatedQRCode, setGeneratedQRCode] = useState<string | null>(null);

  // Function to get liveness session URL
  const getLivenessSessionUrl = useCallback((sid: string): string => {
    const baseUrl = window.location.origin;
    const token = livenessSessionToken?.trim();
    if (!token) {
      return `${baseUrl}/liveness/${sid}`;
    }

    return `${baseUrl}/liveness/${sid}?token=${encodeURIComponent(token)}`;
  }, [livenessSessionToken]);

  // Function to generate QR code
  const generateQRCode = useCallback(async (sid: string): Promise<void> => {
    try {
      const url = getLivenessSessionUrl(sid);
      const qrCode = await QRCode.toDataURL(url);
      setGeneratedQRCode(qrCode.split(',')[1]); // Remove data URL prefix
    } catch (err) {
      console.error('Error generating QR code:', err);
      setError('Failed to generate QR code. Please try again.');
    }
  }, [getLivenessSessionUrl]);

  // Function to check session status
  const checkSessionStatus = useCallback(async (sid: string): Promise<void> => {
    try {
      const response = await apiClient.get<SessionStatus>(
        API_ENDPOINTS.LIVENESS.SESSION_BY_ID(sid, livenessSessionToken)
      );
      setIsActive(response.data.isActive);

      if (!response.data.isActive && onComplete) {
        onComplete();
      }
    } catch (err) {
      console.error('Error checking session status:', err);
      setError('Failed to check session status. Please try again.');
    }
  }, [livenessSessionToken, onComplete]);

  // Initial setup
  useEffect(() => {
    const initializeSession = async () => {
      try {
        // Generate QR code
        await generateQRCode(sessionId);

        // Check initial session status
        await checkSessionStatus(sessionId);
      } catch (err) {
        console.error('Error initializing session:', err);
        setError('Failed to initialize session. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    initializeSession();
  }, [sessionId, generateQRCode, checkSessionStatus]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-4 ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center p-4 ${className}`}>
        <div className="text-red-600 flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!isActive) {
    return (
      <div className={`flex items-center justify-center p-4 ${className}`}>
        <div className="text-gray-600 flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          <p>Session is no longer active</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex flex-col items-center">
        <h2 className="text-xl font-bold mb-2">Scan this QR Code</h2>
        {generatedQRCode && (
          <img
            src={`data:image/png;base64,${generatedQRCode}`}
            alt="QR Code"
            className="w-64 h-64 border border-gray-300 rounded-lg"
          />
        )}
        <p className="mt-2 text-sm text-gray-500">
          Use your mobile device to scan this QR code and complete the liveness check.
        </p>
        <button
          onClick={() => {
            const url = getLivenessSessionUrl(sessionId);
            const width = 600;
            const height = 844;
            const left = (window.screen.width - width) / 2;
            const top = (window.screen.height - height) / 2;
            window.open(
              url,
              'livenessPopup',
              `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
            );
          }}
          className="mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 w-60"
        >
          Open in Browser
        </button>
      </div>
    </div>
  );
};

export default LivenessQRCode;
